/**
 * 优先级队列 — 精细化任务调度
 *
 * 设计目标：
 * - 视口内节点优先加载（high）
 * - 首屏外节点普通优先级（normal）
 * - 预测性预加载低优先级（low）
 *
 * 使用示例：
 *   const queue = new PriorityQueue(6);
 *   queue.add(() => fetchNode(visibleNode), 'high', 'task-1');
 *   queue.add(() => fetchNode(offscreenNode), 'normal', 'task-2');
 */

class PriorityQueue {
  constructor(concurrency = 6) {
    this.concurrency = concurrency;
    this.running = 0;
    this.queues = {
      high: [],
      normal: [],
      low: [],
    };
    this.taskMap = new Map(); // taskId → {resolve, reject, cancelled}
  }

  /**
   * 添加任务到队列
   * @param {Function} task - 异步任务函数 () => Promise<T>
   * @param {string} priority - 优先级: 'high' | 'normal' | 'low'
   * @param {string} taskId - 任务唯一标识（用于取消）
   * @returns {Promise<T>}
   */
  add(task, priority = 'normal', taskId = null) {
    return new Promise((resolve, reject) => {
      const queueItem = {
        task,
        resolve,
        reject,
        priority,
        taskId,
        addedAt: Date.now(),
      };

      // 如果提供了 taskId，记录到 map（用于取消）
      if (taskId) {
        this.taskMap.set(taskId, { resolve, reject, cancelled: false });
      }

      // 根据优先级加入对应队列
      const queue = this.queues[priority] || this.queues.normal;
      queue.push(queueItem);

      // 尝试调度
      this._schedule();
    });
  }

  /**
   * 取消指定任务（如果还在队列中）
   * @param {string} taskId - 任务 ID
   * @returns {boolean} - 是否成功取消
   */
  cancel(taskId) {
    const taskInfo = this.taskMap.get(taskId);
    if (!taskInfo) return false;

    taskInfo.cancelled = true;

    // 从所有队列中移除
    for (const priority of ['high', 'normal', 'low']) {
      const queue = this.queues[priority];
      const index = queue.findIndex(item => item.taskId === taskId);
      if (index !== -1) {
        queue.splice(index, 1);
        taskInfo.reject(new Error(`Task ${taskId} cancelled`));
        this.taskMap.delete(taskId);
        return true;
      }
    }

    return false;
  }

  /**
   * 调度器：从队列中取任务执行
   * @private
   */
  _schedule() {
    // 如果已达到并发上限，不调度
    if (this.running >= this.concurrency) return;

    // 按优先级顺序取任务：high > normal > low
    const item = this._dequeue();
    if (!item) return;

    // 检查是否已被取消
    if (item.taskId) {
      const taskInfo = this.taskMap.get(item.taskId);
      if (taskInfo?.cancelled) {
        console.debug(`⏭️ 任务 ${item.taskId} 已取消，跳过执行`);
        this._schedule(); // 继续调度下一个
        return;
      }
    }

    this.running++;

    // 执行任务
    Promise.resolve()
      .then(() => item.task())
      .then(result => {
        item.resolve(result);
        if (item.taskId) this.taskMap.delete(item.taskId);
      })
      .catch(err => {
        item.reject(err);
        if (item.taskId) this.taskMap.delete(item.taskId);
      })
      .finally(() => {
        this.running--;
        this._schedule(); // 继续调度下一个
      });
  }

  /**
   * 从队列中取出最高优先级的任务
   * @private
   * @returns {Object|null}
   */
  _dequeue() {
    // 优先级顺序：high > normal > low
    if (this.queues.high.length > 0) {
      return this.queues.high.shift();
    }
    if (this.queues.normal.length > 0) {
      return this.queues.normal.shift();
    }
    if (this.queues.low.length > 0) {
      return this.queues.low.shift();
    }
    return null;
  }

  /**
   * 获取队列状态（调试用）
   * @returns {Object}
   */
  getStatus() {
    return {
      running: this.running,
      concurrency: this.concurrency,
      pending: {
        high: this.queues.high.length,
        normal: this.queues.normal.length,
        low: this.queues.low.length,
        total: this.queues.high.length + this.queues.normal.length + this.queues.low.length,
      },
    };
  }

  /**
   * 清空所有队列（组件卸载时调用）
   */
  clear() {
    for (const priority of ['high', 'normal', 'low']) {
      const queue = this.queues[priority];
      for (const item of queue) {
        item.reject(new Error('Queue cleared'));
        if (item.taskId) this.taskMap.delete(item.taskId);
      }
      queue.length = 0;
    }
  }
}

export default PriorityQueue;
